<script setup lang="ts">
import { useElementSize } from "@vueuse/core";
import { defineAsyncComponent } from "vue";
import { z } from "zod";
import type { FormSubmitEvent } from "@nuxt/ui";
import { TURNSTILE_ACTIONS } from "#shared/constants/turnstile";

const VuePdfEmbed = defineAsyncComponent(() => import("vue-pdf-embed"));

const router = useRouter();
const toast = useToast();
const { $csrfFetch } = useNuxtApp();

const documentPublicId = ref<string | null>(null);

// Cloudflare Turnstile
const fileTurnstileRef = ref<{ reset: () => void } | null>(null);
const fileTurnstileToken = ref<string | null>(null);

const endorsementTurnstileRef = ref<{ reset: () => void } | null>(null);
const endorsementTurnstileToken = ref<string | null>(null);

const isFileTurnstileReady = computed(() => !!fileTurnstileToken.value);
const isEndorsementTurnstileReady = computed(
  () => !!endorsementTurnstileToken.value,
);

const waitForTurnstileToken = (
  tokenRef: { value: string | null },
  timeout = 30000,
) => {
  if (tokenRef.value) {
    return Promise.resolve(tokenRef.value);
  }

  return new Promise<string>((resolve, reject) => {
    const stop = watch(
      () => tokenRef.value,
      (value) => {
        if (!value) {
          return;
        }

        clearTimeout(timer);
        stop();
        resolve(value);
      },
      { immediate: true },
    );

    const timer = window.setTimeout(() => {
      stop();
      reject(new Error("La verificación de seguridad aún no está lista."));
    }, timeout);
  });
};

const resetFileTurnstile = () => {
  fileTurnstileToken.value = null;
  if (fileTurnstileRef.value?.reset) {
    fileTurnstileRef.value.reset();
  }
};

const resetEndorsementTurnstile = () => {
  endorsementTurnstileToken.value = null;
  if (endorsementTurnstileRef.value?.reset) {
    endorsementTurnstileRef.value.reset();
  }
};

// Tabs
const tabs = [
  {
    key: "file",
    label: "Subir archivo",
    icon: "i-heroicons-document-plus",
    description: "Carga el PDF original.",
  },
];

const activeTab = ref("0");

const activeTabData = computed(() => tabs[activeTab.value] || tabs[0]);

const stepsPerTab: Record<string, { description: string; image: string }[]> = {
  file: [
    {
      description: "Carga el PDF",
      image: "/steps/select_file.svg",
    },
    {
      description: "Verificamos la huella",
      image: "/steps/validating_file.svg",
    },
    {
      description: "Consulta el resultado",
      image: "/steps/review_info.svg",
    },
  ],
};

const tabContainer = ref(null);
const { height: tabContainerHeight } = useElementSize(tabContainer);
const currentYear = new Date().getFullYear();

// Validation
const isValidating = ref(false);
const isPreparingValidation = ref(false);

const validateInput = async (value: string, token: string) => {
  if (!value) return;

  try {
    const document_hash = value;

    const response = await $csrfFetch("/api/secure/validate", {
      method: "POST",
      body: {
        document_hash,
        token,
      },
    });
    if (response?.public_id) {
      documentPublicId.value = response.public_id;
      toast.add({
        title: "¡Éxito!",
        description: "Documento validado correctamente.",
        color: "success",
        icon: "i-heroicons-check-circle-solid",
      });
    } else {
      toast.add({
        title: "Error",
        description: "No encontramos un registro para este documento.",
        color: "error",
        icon: "i-heroicons-x-circle-solid",
      });
    }
  } catch (err) {
    if (err.status === 404) {
      toast.add({
        title: "Error",
        description: "No encontramos un registro para este documento.",
        color: "error",
        icon: "i-heroicons-x-circle-solid",
      });
      return;
    } else {
      toast.add({
        title: "Error",
        description: "Ocurrió un error al validar el documento.",
        color: "error",
        icon: "i-heroicons-x-circle-solid",
      });
    }
  }
};

// File validation
const fileForm = reactive({
  file: null,
  name: "",
  mimeType: "",
});

const pdfBase64 = ref<any>(null);
const validatingFileName = ref("");

const fileState = computed(() => {
  if (isValidating.value) return "validating";
  const noFile = !fileForm.file;
  const isConfirmed = !!documentPublicId.value;

  if (noFile) return "idle";
  if (isConfirmed) return "confirmed";
  return "fallback";
});

const fileValidationStep = computed(() => {
  if (isPreparingValidation.value) {
    return {
      title: "Preparando validación",
      description: "Estamos iniciando la verificación de seguridad y preparando el archivo.",
      steps: [
        { label: "1. Iniciamos seguridad", active: true },
        { label: "2. Calculamos huella", active: false },
        { label: "3. Consultamos resultado", active: false },
      ],
    };
  }

  return {
    title: "Verificando documento",
    description: "Calculamos la huella del PDF y consultamos el resultado registrado.",
    steps: [
      { label: "1. Validamos seguridad", active: false },
      { label: "2. Calculamos huella", active: false },
      { label: "3. Consultamos resultado", active: true },
    ],
  };
});

const validationFile = async (file: File) => {
  if (!file) {
    fileForm.file = null;
    return false;
  }
  if (file.type !== "application/pdf") {
    toast.add({
      title: "Error",
      description: "Solo se admiten archivos en formato PDF.",
      color: "error",
      icon: "i-heroicons-x-circle-solid",
    });
    return false;
  }
  return true;
};

const changeFile = async (event: Event | any) => {
  let file: any = null;
  if ((event.target as HTMLInputElement).files) {
    file = (event.target as HTMLInputElement).files![0];
  }
  if (event.dataTransfer) {
    file = event.dataTransfer.files[0];
  }
  const result = await validationFile(file);
  if (!result) return;

  await validateFile(file);
};

const validateFile = async (file: File | null) => {
  if (!file) {
    toast.add({
      title: "Error",
      description: "Selecciona un archivo PDF para continuar.",
      color: "error",
      icon: "i-heroicons-x-circle-solid",
    });
    return;
  }

  try {
    validatingFileName.value = file.name;
    isPreparingValidation.value = true;
    const token = await waitForTurnstileToken(fileTurnstileToken);

    if (!token) {
      throw new Error("No fue posible obtener el token de verificación.");
    }

    fileTurnstileToken.value = token;
    isPreparingValidation.value = false;
    isValidating.value = true;

    const fileBase64 = await blobToBase64(file);
    const hash = await calculateHash(file);

    fileForm.file = file;
    fileForm.name = file.name;
    fileForm.mimeType = file.type;
    pdfBase64.value = fileBase64;

    await validateInput(hash, token);
  } catch (error) {
    toast.add({
      title: "Error",
      description:
        error instanceof Error
          ? error.message
          : "No fue posible completar la verificación de seguridad.",
      color: "error",
      icon: "i-heroicons-x-circle-solid",
    });
    return;
  } finally {
    isPreparingValidation.value = false;
    isValidating.value = false;
    validatingFileName.value = "";
    resetFileTurnstile();
  }
};

const resetFileForm = async () => {
  fileForm.file = null;
  fileForm.name = "";
  fileForm.mimeType = "";
  pdfBase64.value = "";
  isPreparingValidation.value = false;
  validatingFileName.value = "";
  documentPublicId.value = null;
  resetFileTurnstile();
};

// Drag & Drop
const dragover = (event: any) => {
  event.preventDefault();
  if (!event.currentTarget.classList.contains("border-primary")) {
    event.currentTarget.classList.remove("border-neutral-300");
    event.currentTarget.classList.add("border-primary");
  }
};

const dragleave = (event: any) => {
  event.currentTarget.classList.add("border-neutral-300");
  event.currentTarget.classList.remove("border-primary");
};

const drop = async (event: any) => {
  event.preventDefault();
  event.currentTarget.classList.add("border-neutral-300");
  event.currentTarget.classList.remove("border-primary");
  await changeFile(event);
};

const goToDetails = () => {
  if (!documentPublicId.value) return;
  router.push({
    path: `/details/${documentPublicId.value}`,
  });
};

// Endorsement validation
const endorsementSchema = z.object({
  endorsementAddress: z
    .string()
    .min(32, "La dirección es demasiado corta")
    .max(64, "La dirección es demasiado larga")
    .regex(/^[1-9A-HJ-NP-Za-km-z]+$/, "La dirección no es válida"),
});

type EndorsementSchema = z.output<typeof endorsementSchema>;

const formState = reactive({
  endorsementAddress: "",
});

const endorsementPublicId = ref<string | null>(null);

const endorsementState = ref<"input" | "validating" | "confirmed">("input");

const validateEndorsement = async (endorsementAddress: string) => {
  if (!endorsementAddress) return;

  try {
    endorsementState.value = "validating";

    const clToken = await waitForTurnstileToken(endorsementTurnstileToken);

    const response = await $csrfFetch("/api/secure/validate-endorsement", {
      method: "POST",
      body: {
        publicKey: endorsementAddress,
        token: clToken,
      },
    });

    if (response?.ok && response.public_id) {
      endorsementPublicId.value = response.public_id;
      endorsementState.value = "confirmed";
      toast.add({
        title: "¡Éxito!",
        description: "Endoso validado correctamente.",
        color: "success",
        icon: "i-heroicons-check-circle-solid",
      });
      goToEndorsementDetails();
    } else {
      endorsementState.value = "input";
      resetEndorsementTurnstile();
      toast.add({
        title: "Error",
        description: "No encontramos un endoso válido en Eclipse Network.",
        color: "error",
        icon: "i-heroicons-x-circle-solid",
      });
    }
  } catch (error) {
    endorsementState.value = "input";
    toast.add({
      title: "Error",
      description:
        error?.data?.error || "Ocurrió un error al validar el endoso digital.",
      color: "error",
      icon: "i-heroicons-x-circle-solid",
    });
  } finally {
    resetEndorsementTurnstile();
  }
};

const onSubmit = (event: FormSubmitEvent<EndorsementSchema>) => {
  const { endorsementAddress } = event.data;

  validateEndorsement(endorsementAddress);
};

const goToEndorsementDetails = () => {
  if (!endorsementPublicId.value) return;

  router.push({
    path: `/details/${endorsementPublicId.value}`,
  });
};

// FAQ items
const faqItems = ref([
  {
    label: "¿Cómo puedo validar un documento firmado?",
    icon: "i-lucide-circle-help",
    content:
      "Carga el PDF original en esta página. Nuestro sistema calcula su huella digital, la contrasta con el registro disponible y te muestra el resultado de la validación junto con el detalle del documento.",
  },
  {
    label: "¿Qué requisitos tiene la carga directa del documento?",
    icon: "i-lucide-circle-help",
    content:
      "Aceptamos archivos PDF. Para obtener un resultado confiable, el documento debe ser el original tal como fue emitido o firmado. Si el contenido fue modificado, la validación no coincidirá.",
  },
  {
    label: "¿Cómo funciona la validación del archivo?",
    icon: "i-lucide-circle-help",
    content:
      "Calculamos la huella digital del PDF y la comparamos con la información registrada para ese documento. Si ambos valores coinciden, puedes confirmar que el archivo corresponde al documento esperado.",
  },
  {
    label: "¿Qué sucede si el documento fue modificado después de la firma?",
    icon: "i-lucide-circle-help",
    content:
      "Cualquier cambio en el contenido del PDF altera su huella digital. Si la huella calculada no coincide con la registrada, el documento no se considerará válido.",
  },
  {
    label: "¿Cómo consulto un endoso digital?",
    icon: "i-lucide-circle-help",
    content:
      "Ingresa la dirección del token emitido en Eclipse Network. Si el endoso es válido y está asociado a un documento registrado, te mostraremos el documento relacionado y el detalle del endoso.",
  },
  {
    label: "¿Cómo protege la plataforma mis datos personales?",
    icon: "i-lucide-circle-help",
    content:
      "Aplicamos controles de seguridad para proteger la información utilizada durante la validación. Solo procesamos los datos necesarios para verificar el documento o el endoso y no compartimos el contenido con terceros sin autorización.",
  },
]);

useHead({
  title: "Valida tu documento",
  meta: [
    {
      name: "description",
      content:
        "Valida documentos electrónicos y consulta endosos digitales de forma rápida y segura.",
    },
    {
      name: "keywords",
      content:
        "validación, firma digital, documento electrónico, PDF, endoso digital, eclipse network",
    },
  ],
});
</script>

<template>
  <UPageSection
    title="Valida tu documento"
    align="left"
    orientation="horizontal"
    :ui="{
      container:
        'flex flex-col lg:grid lg:items-stretch pt-4 pb-14 sm:py-14 md:py-16 lg:py-16 xl:py-16 gap-8 sm:gap-16 px-0!',
      wrapper: 'order-last md:order-first',
    }"
  >
    <template #description>
      <div class="flex h-full flex-col">
        <div>
          <p>
            Confirma tu documento
            <span
              :class="activeTabData.key === 'file' ? 'text-(--ui-primary)' : ''"
            >
              con el PDF original.
            </span>
          </p>
        </div>
        <div class="mt-4 lg:mt-auto">
          <Transition
            mode="out-in"
            enter-active-class="transition-all duration-300"
            enter-from-class="opacity-0 translate-y-2"
            enter-to-class="opacity-100"
            leave-active-class="transition-all duration-300 translate-y-2 delay-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <UPageGrid
              v-if="stepsPerTab[activeTabData.key]"
              :key="activeTabData.key"
            >
              <UPageCard
                v-for="(step, index) in stepsPerTab[activeTabData.key]"
                :key="step.description"
                :description="step.description"
                variant="soft"
                reverse
              >
                <template #default>
                  <div class="h-36">
                    <img
                      :src="step.image"
                      :alt="step.description"
                      class="object-contain w-full h-full"
                    />
                  </div>
                </template>
              </UPageCard>
            </UPageGrid>
          </Transition>
        </div>
      </div>
    </template>

    <!-- Interaction method -->
    <div class="flex h-full flex-col">
      <Transition
        mode="out-in"
        enter-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-y-2"
        enter-to-class="opacity-100"
        leave-active-class="transition-all duration-300 translate-y-2"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <UCard
          :key="activeTabData.key"
          class="flex-1 flex flex-col min-h-0 mt-2"
          :ui="{
            body: 'sm:flex-1 flex flex-col min-h-0',
          }"
        >
          <div
            ref="tabContainer"
            class="flex flex-col items-center justify-center w-full h-[60vh] sm:[40vh] lg:h-full min-h-0"
          >
            <!-- File Validation -->
            <template v-if="activeTabData.key === 'file'">
              <Transition
                mode="out-in"
                enter-active-class="transition-all duration-500 ease-out"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-all duration-300 ease-in"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <!-- Upload Area -->
                <div
                  v-if="fileState === 'idle'"
                  key="upload"
                  class="relative flex items-center justify-center w-full h-full pb-1 border-2 border-neutral-300 border-dashed rounded-md transition-all duration-300 hover:border-primary hover:bg-primary/5"
                  @dragover="dragover"
                  @dragleave="dragleave"
                  @drop="drop"
                >
                  <div
                    v-if="isPreparingValidation"
                    class="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/90 p-4 dark:bg-neutral-950/90"
                  >
                    <div
                      class="flex w-full max-w-lg flex-col gap-5 rounded-2xl border border-neutral-200 bg-white/90 p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/80"
                    >
                      <div class="flex items-start justify-between gap-4">
                        <div class="min-w-0 space-y-1">
                          <p class="text-sm font-medium text-highlighted">
                            {{ fileValidationStep.title }}
                          </p>
                          <p class="truncate text-sm text-neutral-600 dark:text-neutral-400">
                            {{ validatingFileName || "Preparando PDF..." }}
                          </p>
                          <p class="text-sm text-neutral-500 dark:text-neutral-400">
                            {{ fileValidationStep.description }}
                          </p>
                        </div>
                        <div
                          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"
                        >
                          <div
                            class="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin"
                          />
                        </div>
                      </div>

                      <div class="grid gap-2 text-left sm:grid-cols-3">
                        <div
                          v-for="step in fileValidationStep.steps"
                          :key="step.label"
                          :class="
                            step.active
                              ? 'rounded-lg bg-primary/10 p-3 text-sm text-primary ring-1 ring-primary/20'
                              : 'rounded-lg bg-neutral-50 p-3 text-sm text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-950/60 dark:text-neutral-300 dark:ring-neutral-800'
                          "
                        >
                          {{ step.label }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-2 text-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-12 h-12 mx-auto text-neutral-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1.5"
                        d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div class="flex text-sm text-neutral-600">
                      <label
                        for="file-upload"
                        class="relative font-medium bg-white rounded-md cursor-pointer text-(--ui-primary) hover:text-(--ui-primary) focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-0 focus-within:ring-primary"
                      >
                        <span>{{
                          isPreparingValidation
                            ? "Preparando validación..."
                            : fileForm.name
                              ? "Cambiar"
                              : "Elegir PDF"
                        }}</span>
                        <input
                          :required="!fileForm.file"
                          :disabled="
                            !isFileTurnstileReady ||
                            isPreparingValidation ||
                            isValidating
                          "
                          accept="application/pdf"
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          class="sr-only"
                          @change="changeFile"
                        />
                      </label>
                      <p class="pl-1 break-all">
                        {{
                          fileForm.name ? fileForm.name : "o arrástralo aquí"
                        }}
                      </p>
                    </div>
                    <div class="text-sm text-neutral-600">
                      <span>Solo PDF original.</span>
                    </div>
                  </div>

                  <div
                    v-if="
                      !isFileTurnstileReady &&
                      !isPreparingValidation &&
                      !isValidating
                    "
                    class="absolute inset-0 z-10 flex items-center justify-center rounded-md bg-white/88 p-4 backdrop-blur-[1px] dark:bg-neutral-950/88"
                  >
                    <div
                      class="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white/95 p-5 text-left shadow-sm dark:border-neutral-800 dark:bg-neutral-900/90"
                    >
                      <div class="flex items-start gap-3">
                        <div
                          class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"
                        >
                          <div
                            class="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin"
                          />
                        </div>
                        <div class="space-y-1">
                          <p class="text-sm font-medium text-highlighted">
                            Preparando seguridad
                          </p>
                          <p class="text-sm text-neutral-600 dark:text-neutral-400">
                            Si hace falta, valida aquí mismo.
                          </p>
                        </div>
                      </div>

                      <div class="mt-4 flex justify-center">
                        <ClientOnly>
                          <NuxtTurnstile
                            ref="fileTurnstileRef"
                            v-model="fileTurnstileToken"
                            trigger="immediate"
                            :options="{
                              theme: 'light',
                              action: TURNSTILE_ACTIONS.validateFile,
                              appearance: 'interaction-only',
                            }"
                          />
                          <template #fallback>
                            <USkeleton class="h-[65px] w-[300px] rounded-xl" />
                          </template>
                        </ClientOnly>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Validating -->
                <div
                  v-else-if="fileState === 'validating'"
                  class="flex w-full h-full flex-col items-center justify-center"
                >
                  <div
                    class="flex w-full max-w-lg flex-col gap-5 rounded-2xl border border-neutral-200 bg-white/90 p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900/80"
                  >
                    <div class="flex items-start justify-between gap-4">
                      <div class="min-w-0 space-y-1">
                        <p class="text-sm font-medium text-highlighted">
                          {{ fileValidationStep.title }}
                        </p>
                        <p class="truncate text-sm text-neutral-600 dark:text-neutral-400">
                          {{ validatingFileName || "Preparando PDF..." }}
                        </p>
                        <p class="text-sm text-neutral-500 dark:text-neutral-400">
                          {{ fileValidationStep.description }}
                        </p>
                      </div>
                      <div
                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"
                      >
                        <div
                          class="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin"
                        />
                      </div>
                    </div>

                    <div class="grid gap-2 text-left sm:grid-cols-3">
                      <div
                        v-for="step in fileValidationStep.steps"
                        :key="step.label"
                        :class="
                          step.active
                            ? 'rounded-lg bg-primary/10 p-3 text-sm text-primary ring-1 ring-primary/20'
                            : 'rounded-lg bg-neutral-50 p-3 text-sm text-neutral-600 ring-1 ring-neutral-200 dark:bg-neutral-950/60 dark:text-neutral-300 dark:ring-neutral-800'
                        "
                      >
                        {{ step.label }}
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Confirmed with Overlay -->
                <div
                  v-else-if="fileState === 'confirmed'"
                  class="relative flex-1 w-full h-full flex flex-col items-center justify-center overflow-hidden"
                >
                  <!-- PDF Background -->
                  <div
                    class="absolute inset-0 w-full h-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center"
                    :style="{ height: `${tabContainerHeight}px` }"
                  >
                    <client-only placeholder="Cargando vista previa del PDF">
                      <VuePdfEmbed
                        :source="pdfBase64"
                        :page="1"
                        class="w-full h-full object-cover"
                      />
                    </client-only>
                  </div>

                  <!-- Translucent Overlay with Buttons -->
                  <div
                    class="absolute inset-0 bg-neutral-500/10 backdrop-blur-[0px] flex items-end justify-center z-10 py-6"
                  >
                    <div class="flex flex-col md:flex-row gap-4 items-center">
                      <UButton
                        label="Validar otro documento"
                        size="xl"
                        color="neutral"
                        icon="i-heroicons-arrow-path"
                        @click="resetFileForm"
                      />
                      <UButton
                        label="Ver documento"
                        size="xl"
                        icon="i-heroicons-arrow-top-right-on-square"
                        :disabled="isValidating"
                        @click="goToDetails"
                      />
                    </div>
                  </div>
                </div>
                <!-- Fallback -->
                <div
                  v-else
                  key="fallback"
                  class="flex flex-col items-center justify-center w-full h-full gap-4"
                >
                  <UIcon
                    name="i-heroicons-document-minus"
                    class="size-12 text-neutral-400"
                  />
                  <p class="text-neutral-500 italic">
                    No fue posible validar el documento con la información
                    disponible.
                  </p>
                  <UButton
                    label="Volver a intentar"
                    size="lg"
                    @click="resetFileForm"
                  />
                </div>
              </Transition>
            </template>
          </div>
        </UCard>
      </Transition>
    </div>
  </UPageSection>
  <!-- Digital endorsement -->
  <UPageSection
    title="Consulta endosos digitales"
    align="left"
    orientation="horizontal"
    :ui="{
      container:
        'flex flex-col lg:grid pt-8 pb-14 sm:pt-8 sm:pb-14  md:pt-8 md:pb-14  lg:py-14 xl:py-14 gap-8 sm:gap-16 px-0!',
      wrapper: 'order-first lg:order-last',
    }"
  >
    <template #description>
      <p>
        Consulta un <strong>endoso digital</strong> usando la
        <span class="text-(--ui-primary)"
          >dirección del token en Eclipse Network</span
        >. Obtendrás el documento relacionado y el detalle del endoso
        registrado.
      </p>
    </template>
    <div class="flex flex-col">
      <UCard
        class="flex-1 flex flex-col min-h-0 lg:mt-2"
        variant="soft"
        :ui="{
          body: 'sm:flex-1 flex flex-col min-h-0',
        }"
      >
        <UForm
          :schema="endorsementSchema"
          :state="formState"
          @submit="onSubmit"
          class="relative flex flex-col gap-4 items-end"
        >
          <div
            v-if="!isEndorsementTurnstileReady"
            class="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/88 p-4 backdrop-blur-[1px] dark:bg-neutral-950/88"
          >
            <div
              class="w-full max-w-sm rounded-2xl border border-neutral-200 bg-white/95 p-5 text-left shadow-sm dark:border-neutral-800 dark:bg-neutral-900/90"
            >
              <div class="flex items-start gap-3">
                <div
                  class="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"
                >
                  <div
                    class="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin"
                  />
                </div>
                <div class="space-y-1">
                  <p class="text-sm font-medium text-highlighted">
                    Preparando seguridad
                  </p>
                  <p class="text-sm text-neutral-600 dark:text-neutral-400">
                    Si hace falta, valida aquí mismo.
                  </p>
                </div>
              </div>

              <div class="mt-4 flex justify-center">
                <ClientOnly>
                  <NuxtTurnstile
                    ref="endorsementTurnstileRef"
                    v-model="endorsementTurnstileToken"
                    trigger="immediate"
                    :options="{
                      theme: 'light',
                      action: TURNSTILE_ACTIONS.validateEndorsement,
                      appearance: 'interaction-only',
                    }"
                  />
                  <template #fallback>
                    <USkeleton class="h-[65px] w-[300px] rounded-xl" />
                  </template>
                </ClientOnly>
              </div>
            </div>
          </div>

          <UFormField
            name="endorsementAddress"
            label="Ingresa la dirección del endoso digital"
            class="w-full"
          >
            <UInput
              v-model.trim="formState.endorsementAddress"
              size="xl"
              placeholder="Ej. 9Qx7A2MZKpE5R4T8NwJdFvB3C…"
              class="w-full"
              :disabled="
                endorsementState === 'validating' || !isEndorsementTurnstileReady
              "
            />
          </UFormField>
          <!-- Cloudflare -->
          <UButton
            type="submit"
            size="xl"
            class="max-w-fit lg:px-4"
            color="primary"
            :disabled="
              !formState.endorsementAddress || !isEndorsementTurnstileReady
            "
            :loading="
              endorsementState === 'validating' ||
              endorsementState === 'confirmed'
            "
          >
            Validar
          </UButton>
        </UForm>
      </UCard>
    </div>
  </UPageSection>
  <UPageHeader title="Preguntas frecuentes" class="lg:mt-8" />
  <UAccordion
    :items="faqItems"
    :unmount-on-hide="false"
    :ui="{
      trigger: 'text-base',
      body: 'text-base text-muted',
    }"
  />
  <UFooter class="mt-12 border-t border-default pt-6">
    <template #bottom>
      <div
        class="flex flex-col gap-2 text-xs text-muted sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
          <a
            href="https://www.remtools.io/pdf/usuarios_aviso_de_privacidad_usuarios_rem.pdf"
            target="_blank"
            rel="noopener noreferrer"
            class="hover:text-primary transition-colors"
          >
            Aviso de privacidad
          </a>
          <a
            href="https://www.remtools.io/pdf/politica_de_seguridad_de_la_informacion.pdf"
            target="_blank"
            rel="noopener noreferrer"
            class="hover:text-primary transition-colors"
          >
            Política de seguridad de la información
          </a>
        </div>
        <p>Plataforma de validación para documentos y endosos digitales.</p>
      </div>
    </template>
  </UFooter>
</template>
